import {
  Download,
  FileCheck,
  FileText,
  FileType,
  Image,
  MoreVerticalIcon,
  Pencil,
  Share2,
  Timer,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import {
  PublicRecording,
  RoleEnum,
  RoleGroupEnum,
} from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useGenerateChapters } from "../api/generate-chapters";
import { useGenerateSummary } from "../api/generate-summary";
import { getRecordingQueryKey } from "../api/get-recording";
import { useMarkRecordingAsPermanent } from "../api/mark-recording-as-permanent";
import { useMarkRecordingAsTemporary } from "../api/mark-recording-as-temporary";

import { DeleteRecordingModal } from "./delete-recording-modal";
import { ModifyThumbnailModal } from "./modify-thumbnail-modal";
import { ShareRecordingModal } from "./share-recording-modal";

type RecordingPageActionsProps = {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  recording: PublicRecording;
};

export function RecordingPageActions(props: RecordingPageActionsProps) {
  const { hasPermission, organisation } = useOrganisation();
  const queryClient = useQueryClient();
  const [modalType, setModalType] = useState<
    "delete" | "share" | "modify-thumbnail" | null
  >(null);
  const recordingId = props.recording.id;
  const videoUrl = props.recording.videoURL;
  const hasTranscript = !!props.recording.transcript;
  const hasSummary = !!props.recording.summary;
  const hasChapters = !!props.recording.chapters;
  /**
   * Whether the recording is owned by the users actual organisation
   */
  const isOwner = props.recording.organisation.id === organisation.id;

  const { mutateAsync: generateSummary, isPending: isGeneratingSummary } =
    useGenerateSummary(recordingId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getRecordingQueryKey(organisation.id, recordingId),
        });
      },
    });
  const { mutateAsync: generateChapters, isPending: isGeneratingChapters } =
    useGenerateChapters(organisation.id, recordingId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getRecordingQueryKey(organisation.id, recordingId),
        });
      },
    });

  const {
    mutateAsync: markRecordingAsPermanent,
    isPending: isMarkingRecordingAsPermanent,
  } = useMarkRecordingAsPermanent({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getRecordingQueryKey(organisation.id, recordingId),
      });
    },
  });

  const {
    mutateAsync: markRecordingAsTemporary,
    isPending: isMarkingRecordingAsTemporary,
  } = useMarkRecordingAsTemporary({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getRecordingQueryKey(organisation.id, recordingId),
      });
    },
  });
  const handleGenerateSummary = () => {
    const promise = generateSummary({ recordingId });
    toast.promise(promise, {
      loading: "Generating summary...",
      success: "Summary generated successfully",
      error: "Failed to generate summary",
    });
  };

  const handleGenerateChapters = () => {
    const promise = generateChapters({
      orgId: organisation.id,
      recordingId,
    });
    toast.promise(promise, {
      loading: "Generating chapters...",
      success: "Chapters generated successfully",
      error: "Failed to generate chapters",
    });
  };

  const handleMarkRecordingAsPermanent = () => {
    const promise = markRecordingAsPermanent({
      orgId: organisation.id,
      id: recordingId,
    });
    toast.promise(promise, {
      loading: "Marking recording as permanent...",
      success: "Recording marked as permanent",
      error: "Failed to mark recording as permanent",
    });
  };

  const handleMarkRecordingAsTemporary = () => {
    const promise = markRecordingAsTemporary({
      orgId: organisation.id,
      id: recordingId,
    });
    toast.promise(promise, {
      loading: "Marking recording as temporary...",
      success: "Recording marked as temporary",
      error: "Failed to mark recording as temporary",
    });
  };

  const canUserDeleteRecording =
    (hasPermission(RoleEnum.CALL_MANAGEMENT) ||
      hasPermission(RoleEnum.ORGANISATION_MANAGEMENT)) &&
    isOwner;

  const canDownloadRecording =
    (hasPermission(RoleEnum.REPLAY) ||
      hasPermission(RoleEnum.REPLAY_ADVANCED) ||
      organisation.roleGroup === RoleGroupEnum.ADMIN) &&
    organisation.roleGroup !== RoleGroupEnum.VIEWER &&
    isOwner;

  const canUserModifyThumbnail =
    hasPermission(RoleEnum.CALL_MANAGEMENT) &&
    (hasPermission(RoleEnum.REPLAY) ||
      hasPermission(RoleEnum.REPLAY_ADVANCED)) &&
    isOwner;

  const canUserGenerateChapters =
    (hasPermission(RoleEnum.REPLAY) ||
      hasPermission(RoleEnum.REPLAY_ADVANCED)) &&
    hasPermission(RoleEnum.CALL_MANAGEMENT) &&
    isOwner;

  const canUserGenerateSummary =
    (hasPermission(RoleEnum.REPLAY) ||
      hasPermission(RoleEnum.REPLAY_ADVANCED)) &&
    hasPermission(RoleEnum.CALL_MANAGEMENT) &&
    isOwner;

  const canUserEditName = hasPermission(RoleEnum.CALL_MANAGEMENT) && isOwner;
  const canUserMarkAsPermanent =
    !props.recording.isPermanent &&
    hasPermission(RoleEnum.CALL_MANAGEMENT) &&
    isOwner;
  const canUserMarkAsTemporary =
    props.recording.isPermanent &&
    hasPermission(RoleEnum.CALL_MANAGEMENT) &&
    isOwner;

  if (!isOwner) {
    return null;
  }

  return (
    <Dialog open={!!modalType} onOpenChange={() => setModalType(null)}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon className="size-4" aria-hidden={true} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canUserEditName && (
            <DropdownMenuItem onClick={() => props.setIsEditing(true)}>
              <Pencil className="size-4" aria-hidden={true} /> Edit name
            </DropdownMenuItem>
          )}
          {canUserMarkAsPermanent && (
            <DropdownMenuItem
              onClick={handleMarkRecordingAsPermanent}
              disabled={isMarkingRecordingAsPermanent}
            >
              <FileCheck className="size-4" aria-hidden={true} /> Mark as
              permanent
            </DropdownMenuItem>
          )}
          {canUserMarkAsTemporary && (
            <DropdownMenuItem
              onClick={handleMarkRecordingAsTemporary}
              disabled={isMarkingRecordingAsTemporary}
            >
              <Timer className="size-4" aria-hidden={true} /> Mark as temporary
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setModalType("share")}>
            <Share2 className="size-4" aria-hidden={true} /> Share
          </DropdownMenuItem>

          {canDownloadRecording && (
            <DropdownMenuItem asChild>
              <a href={videoUrl} download target="_blank">
                <Download className="size-4" aria-hidden={true} /> Download
              </a>
            </DropdownMenuItem>
          )}

          {canUserModifyThumbnail && (
            <DropdownMenuItem onClick={() => setModalType("modify-thumbnail")}>
              <Image className="size-4" aria-hidden={true} /> Modify Thumbnail
            </DropdownMenuItem>
          )}
          {hasTranscript && (
            <>
              {canUserGenerateSummary && (
                <DropdownMenuItem
                  onClick={handleGenerateSummary}
                  disabled={hasSummary || isGeneratingSummary}
                >
                  <FileType className="size-4" aria-hidden={true} /> Generate
                  Summary
                </DropdownMenuItem>
              )}
              {canUserGenerateChapters && (
                <DropdownMenuItem
                  onClick={handleGenerateChapters}
                  disabled={hasChapters || isGeneratingChapters}
                >
                  <FileText className="size-4" aria-hidden={true} /> Generate
                  Chapters
                </DropdownMenuItem>
              )}
            </>
          )}
          {canUserDeleteRecording && (
            <>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setModalType("delete")}
              >
                <Trash2 className="size-4" aria-hidden={true} /> Delete
                Recording
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {modalType === "delete" && (
        <DeleteRecordingModal recordingId={recordingId} />
      )}
      {modalType === "share" && (
        <ShareRecordingModal recordingId={recordingId} />
      )}
      {modalType === "modify-thumbnail" && (
        <ModifyThumbnailModal
          videoURL={videoUrl}
          recordingId={recordingId}
          handleModalClose={() => setModalType(null)}
        />
      )}
    </Dialog>
  );
}
